import {
  OnDestroy,
  OnInit,
  Injector,
  Directive,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { firstValueFrom, Observable } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import {
  ContentActionRef,
  DocumentListPresetRef,
} from '@alfresco/adf-extensions';
import {
  FileUploadEvent,
  UploadService,
  RowFilter,
  ShareDataRow,
  UploadFilesEvent,
} from '@alfresco/adf-content-services';
import { NodeEntry, PathElement, Node } from '@alfresco/js-api';

import {
  canBatchMetadataUpdate,
  getAppSelection,
  getCurrentFolder,
  getDocumentDisplayMode,
  isAdmin,
  isInfoDrawerOpened,
} from '../../../store/selectors/app.custom-selectors';
import { NodeInfoService } from '../../../services/node.service';
import { CustomContentManagementService } from '../../../services/custom-content-management.service';
import { imageResolver } from '../../../utils/image-resolver.utils';
import { WkCoreExtensionService } from '../../../services/wk-core-extensions.service';
import { WkFilesComponent } from '../wk-files/wk-files.component';
import { WkContentApiService } from '../../../services/wk-content-api.service';
import { WkNodeActionsService } from '../../../services/wk-node-actions.service';
import { SetCurrentFolderAction } from '../../../store/actions/app.custom-actions';
import { WkDocumentListComponent } from '../wk-document-list/wk-document-list.component';
import { WkFileUploadEventService } from '../../../services/wk-file-upload-event.service';

@Directive()
export class WkCustomFilesComponent
  extends WkFilesComponent
  implements OnInit, OnDestroy
{
  @ViewChild('wkDocumentListRef')
  wkDocumentList: WkDocumentListComponent;
  isValidPath = true;
  isSmallScreen = false;
  isAdmin = false;
  fileNamesToUpload: string[];
  uploadedFromSideNav = false;
  fileUploadedFromSideNav = [];

  imageResolveFunction = imageResolver;

  canBatchMetadataUpdate$: Observable<boolean>;

  columns: any[] = [];
  folderFilter: RowFilter;

  private customNodePath: PathElement[];

  static isLockedNode(node) {
    return (
      node.isLocked ||
      (node.properties && node.properties['cm:lockType'] === 'READ_ONLY_LOCK')
    );
  }
  private readonly customRouter: Router;
  private readonly customRoute: ActivatedRoute;
  private readonly customContentApi: WkContentApiService;
  private readonly customNodeActionsService: WkNodeActionsService;
  private readonly customUploadService: UploadService;
  private readonly wkExtensionService: WkCoreExtensionService;
  private readonly wkFileUploadEventService: WkFileUploadEventService;

  private readonly customBreakpointObserver: BreakpointObserver;
  private readonly customContent: CustomContentManagementService;
  private readonly nodeInfoService: NodeInfoService;

  constructor(injector: Injector) {
    super(injector);
    this.wkExtensionService = injector.get<WkCoreExtensionService>(
      WkCoreExtensionService
    );
    this.customRouter = injector.get<Router>(Router);
    this.customRoute = injector.get<ActivatedRoute>(ActivatedRoute);
    this.customContentApi =
      injector.get<WkContentApiService>(WkContentApiService);
    this.customNodeActionsService =
      injector.get<WkNodeActionsService>(WkNodeActionsService);
    this.customUploadService = injector.get<UploadService>(UploadService);
    this.customBreakpointObserver =
      injector.get<BreakpointObserver>(BreakpointObserver);
    this.customContent = injector.get<CustomContentManagementService>(
      CustomContentManagementService
    );
    this.nodeInfoService = injector.get<NodeInfoService>(NodeInfoService);
    this.wkFileUploadEventService = injector.get<WkFileUploadEventService>(
      WkFileUploadEventService
    );

    this.folderFilter = (row: ShareDataRow) => {
      const node = row.node.entry;

      if (
        node &&
        node.aspectNames.indexOf('cm:checkedOut') === -1 &&
        node.aspectNames.indexOf('sys:hidden') === -1
      ) {
        return true;
      }

      return false;
    };
  }

  ngOnInit() {
    const {
      customRoute,
      content,
      customContent,
      customNodeActionsService,
      customUploadService,
    } = this;
    const { data } = customRoute.snapshot;

    this.title = data.title;

    this.infoDrawerOpened$ = this.store.select(isInfoDrawerOpened);
    this.canBatchMetadataUpdate$ = this.store.select(canBatchMetadataUpdate);

    customRoute.queryParamMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((queryMap: Params) => {
        this.queryParams = queryMap.params;
      });

    customRoute.params
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ folderId }: Params) => {
        const nodeId = folderId || data.defaultNodeId;
        this.customContentApi.getNode(nodeId).subscribe({
          next: (node) => {
            this.isValidPath = true;
            const dummyNode = this.cloneNode(node);
            this.nodeInfoService.setCurrentNode(dummyNode);

            if (node.entry && node.entry.isFolder) {
              this.setCurrentSiteVariableByNode(node);
              this.customUpdateCurrentNode(node.entry);
            } else {
              this.customRouter.navigate(
                ['/personal-files', node.entry.parentId],
                {
                  replaceUrl: true,
                }
              );
            }
          },
          error: () => (this.isValidPath = false),
        });
      });

    customNodeActionsService.contentCopied
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((nodes) => this.onContentCopied(nodes));

    content.nodesDeleted
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.wkDocumentList.reload());

    customContent.nodesCheckouted
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.wkDocumentList.reload());

    customUploadService.fileUploadComplete
      .pipe(debounceTime(300), takeUntil(this.onDestroy$))
      .subscribe((file) => this.onFileUploadedEvent(file));

    customUploadService.fileUploadDeleted
      .pipe(debounceTime(300), takeUntil(this.onDestroy$))
      .subscribe((file) => this.onFileUploadedEvent(file));

    this.customBreakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.isSmallScreen = result.matches;
      });

    this.store
      .select(isAdmin)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.isAdmin = value;
      });
    // TODO: Add to documentation
    this.wkExtensionService.documentListPresets.subscribe(
      (arrayFiles: DocumentListPresetRef[]) => {
        this.columns = arrayFiles;
      }
    );

    this.infoDrawerOpened$ = this.store
      .select(isInfoDrawerOpened)
      .pipe(
        map((infoDrawerState) => !this.isOutletPreviewUrl() && infoDrawerState)
      );

    this.documentDisplayMode$ = this.store.select(getDocumentDisplayMode);

    this.store
      .select(getAppSelection)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selection) => {
        this.selection = selection;
        this.extensions
          .getAllowedToolbarActions()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((actionList: ContentActionRef[]) => {
            this.actions = actionList;
          });
        this.extensions
          .getViewerToolbarActions()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((toolBarActionsList: ContentActionRef[]) => {
            this.viewerToolbarActions = toolBarActionsList;
          });
        this.canUpdateNode =
          this.selection.count === 1 &&
          this.content.canUpdateNode(selection.first);
      });

    this.store
      .select(getCurrentFolder)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((node) => {
        this.canUpload = node && this.content.canUploadContent(node);
      });
  }

  private cloneNode(node: NodeEntry): NodeEntry {
    const dummyPathElements = node.entry.path.elements.map((obj) => ({
      ...obj,
    }));
    const dummyPath = { ...node.entry.path };
    const dummyEntry = { ...node.entry };
    const dummyNode = { ...node };
    dummyPath.elements = dummyPathElements;
    dummyEntry.path = dummyPath;
    dummyNode.entry = dummyEntry;
    return dummyNode;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.clearSiteVariable();
    this.store.dispatch(new SetCurrentFolderAction(null));
  }

  // added this but will be removing it after we upgrade ADF to 4.5.0 or new released version
  navigate(nodeId: string = null) {
    this.wkDocumentList.DEFAULT_PAGINATION.skipCount = 0;
    const currentNodeId = this.customRoute.snapshot.paramMap.get('folderId');
    const urlWithoutParams = decodeURIComponent(this.customRouter.url).split(
      '?'
    )[0];
    const urlToNavigate: string[] = this.getUrlToNavigate(
      urlWithoutParams,
      currentNodeId,
      nodeId
    );
    this.customRouter.navigate(urlToNavigate);
  }

  private getUrlToNavigate(
    currentURL: string,
    currentNodeId: string,
    nextNodeId: string
  ): string[] {
    return currentNodeId
      ? this.getNextNodeUrlToNavigate(currentURL, currentNodeId, nextNodeId)
      : this.appendNextNodeIdToUrl(currentURL, nextNodeId);
  }

  private getNextNodeUrlToNavigate(
    currentURL: string,
    currentNodeId: string,
    nextNodeId: string
  ): string[] {
    const urlToNavigate: string[] =
      nextNodeId && !this.isRootNode(nextNodeId)
        ? this.replaceCurrentNodeIdWithNextNodeId(
            currentURL,
            currentNodeId,
            nextNodeId
          )
        : this.removeNodeIdFromUrl(currentURL, currentNodeId);
    urlToNavigate.shift();
    return urlToNavigate;
  }

  private replaceCurrentNodeIdWithNextNodeId(
    currentURL: string,
    currentNodeId: string,
    nextNodeId: string
  ): string[] {
    const nextNodeUrlToNavigate = currentURL.split('/');
    const index = nextNodeUrlToNavigate.indexOf(currentNodeId);
    if (index > 0) {
      nextNodeUrlToNavigate[index] = nextNodeId;
    }
    return nextNodeUrlToNavigate;
  }

  private removeNodeIdFromUrl(
    currentURL: string,
    currentNodeId: string
  ): string[] {
    const rootUrl: string[] = currentURL.replace(currentNodeId, '').split('/');
    rootUrl.pop();
    return rootUrl;
  }

  private appendNextNodeIdToUrl(currentURL: string, nodeId: string): string[] {
    const navigateToNodeUrl = currentURL.split('/');
    if (nodeId && !this.isRootNode(nodeId)) {
      navigateToNodeUrl.push(nodeId);
    }
    navigateToNodeUrl.shift();
    return navigateToNodeUrl;
  }

  navigateTo(node: NodeEntry) {
    if (node && node.entry) {
      this.selectedNode = node;
      const { id, isFolder } = node.entry;

      if (isFolder) {
        this.navigate(id);
        return;
      }

      if (WkCustomFilesComponent.isLockedNode(node.entry)) {
        this.showPreview(node, { location: this.customRouter.url });
        return;
      }

      this.showPreview(node, { location: this.customRouter.url });
    }
  }

  onFileUploadedEvent(event: FileUploadEvent) {
    const node: NodeEntry = event.file.data;

    // check root and child nodes
    if (node && node.entry && node.entry.parentId === this.getParentNodeId()) {
      this.reload();
      return;
    }

    // check the child nodes to show dropped folder
    if (event && event.file.options.parentId === this.getParentNodeId()) {
      this.displayFolderParent(event.file.options.path, 0);
      return;
    }

    if (event && event.file.options.parentId && this.customNodePath) {
      const correspondingNodePath = this.customNodePath.find(
        (pathItem) => pathItem.id === event.file.options.parentId
      );

      // check if the current folder has the 'trigger-upload-folder' as one of its parents
      if (correspondingNodePath) {
        const correspondingIndex =
          this.customNodePath.length -
          this.customNodePath.indexOf(correspondingNodePath);
        this.displayFolderParent(event.file.options.path, correspondingIndex);
      }
    }
  }

  displayFolderParent(filePath = '', index) {
    const parentName = filePath.split('/')[index];
    const currentFoldersDisplayed: any =
      this.wkDocumentList.data.getRows() || [];

    const alreadyDisplayedParentFolder = currentFoldersDisplayed.find(
      (row) => row.node.entry.isFolder && row.node.entry.name === parentName
    );

    if (alreadyDisplayedParentFolder) {
      return;
    }
    this.wkDocumentList.reload();
  }

  onContentCopied(nodes: NodeEntry[]) {
    const newNode = nodes.find(
      (node) =>
        node && node.entry && node.entry.parentId === this.getParentNodeId()
    );
    if (newNode) {
      this.wkDocumentList.reload();
    }
  }

  // todo: review this approach once 5.2.3 is out
  private async customUpdateCurrentNode(node: Node) {
    this.customNodePath = null;

    if (node && node.path && node.path.elements) {
      const elements = node.path.elements;

      this.customNodePath = elements.map((pathElement) =>
        Object.assign({}, pathElement)
      );

      if (elements.length > 1) {
        if (elements[1].name === 'User Homes') {
          if (!this.isAdmin) {
            elements.splice(0, 2);
          }
        } else if (elements[1].name === 'Sites') {
          await this.customNormalizeSitePath(node);
        }
      }
    }
    this.node = node;
    this.store.dispatch(new SetCurrentFolderAction(node));
  }

  // todo: review this approach once 5.2.3 is out
  private async customNormalizeSitePath(node: Node) {
    const elements = node.path.elements;

    // remove 'Sites'
    elements.splice(1, 1);

    if (this.isSiteContainer(node)) {
      // rename 'documentLibrary' entry to the target site display name
      // clicking on the breadcrumb entry loads the site content
      const parentNode = await firstValueFrom(
        this.customContentApi.getNodeInfo(node.parentId)
      );
      node.name = parentNode.properties['cm:title'] || parentNode.name;

      // remove the site entry
      elements.splice(1, 1);
    } else {
      // remove 'documentLibrary' in the middle of the path
      const selectedElement = elements.filter(function (el) {
        return (
          el.name === 'documentLibrary' ||
          el.name === 'contentTemplates' ||
          el.name === 'emailTemplates' ||
          el.name === 'contractTemplates'
        );
      });
      if (selectedElement.length > 0) {
        const selectedElementIndex = elements.findIndex(
          (el) => el.name === selectedElement[0].name
        );
        if (selectedElement[0].name === 'documentLibrary') {
          const siteFragment = elements[selectedElementIndex - 1];
          const siteNode = await firstValueFrom(
            this.customContentApi.getNodeInfo(siteFragment.id)
          );

          // apply Site Name to the parent fragment
          siteFragment.name = siteNode.properties['cm:title'] || siteNode.name;
          elements.splice(selectedElementIndex, 1);
        } else if (selectedElement[0].name === 'contentTemplates') {
          await this.templateBreadcrumb(elements, selectedElementIndex);
        } else if (selectedElement[0].name === 'emailTemplates') {
          await this.templateBreadcrumb(elements, selectedElementIndex);
        } else if (selectedElement[0].name === 'contractTemplates') {
          await this.templateBreadcrumb(elements, selectedElementIndex);
        }
      }
    }
  }

  async templateBreadcrumb(elements, templateTypeIndex) {
    const siteFragment = elements[templateTypeIndex - 1];
    const siteNode = await firstValueFrom(
      this.customContentApi.getNodeInfo(siteFragment.id)
    );
    const templateNode = await firstValueFrom(
      this.customContentApi.getNodeInfo(elements[templateTypeIndex].id)
    );
    // apply Site Name to the parent fragment
    elements[templateTypeIndex].name = templateNode.properties['cm:title'];
    siteFragment.name = siteNode.properties['cm:title'] || siteNode.name;
  }

  // method to set global site variable used by Walkme plugin
  setCurrentSiteVariableByNode(node: NodeEntry): void {
    const site: PathElement = this.getSiteFromNodePath(node.entry);
    if (site) {
      (window as any).Alfresco.constants.SITE = site.name;
    }
  }

  getSiteFromNodePath(node: Node): PathElement {
    let site;
    if (node.path && node.path.elements) {
      site = node.path.elements.find(
        (pathNode: Node) =>
          pathNode.nodeType === 'st:site' && pathNode.name !== 'Sites'
      );
    }
    return site;
  }

  clearSiteVariable(): void {
    if ((window as any).Alfresco?.constants?.SITE) {
      (window as any).Alfresco.constants.SITE = '';
    }
  }

  onBeginUpload(event: UploadFilesEvent) {
    this.wkFileUploadEventService.triggerUploadFilesEvent(event);
  }
}
